if (!customElements.get('product-section')) {
    customElements.define('variant-picker', class extends HTMLElement {
        initialize() {
            this.product = null;
            this.addEventListener('change', this.updateOptions.bind(this));
        }

        updateOptions() {
            const options = this.getOptions();
            const variant = this.getCurrentVariant(options);

            this.dispatchEvent(new CustomEvent('productVariantChanged', {
                detail: {
                    product: this.getProduct(),
                    variant,
                    options
                },
                bubbles: true
            }));

            this.updateOptionsAvailability(options);
            this.updateActiveOptionValue(options);
        }

        updateOptionsAvailability(currentOptions) {
            if (typeof currentOptions.option2 === 'undefined') {
                // If we only have one product option there is no reason to run this
                return;
            }

            const selectedOptionOneVariants = this.product.variants.filter(
                variant => currentOptions.option1 === variant.option1
            );

            this.getFieldsets().forEach(fieldset => {
                const position = parseInt(fieldset.dataset.position);
                if (position === 1) {
                    return;
                }
                const previousPosition = position - 1;

                const availableOptionValues = selectedOptionOneVariants.filter(
                    variant => variant.available && variant[previousPosition] === currentOptions[previousPosition]
                ).map(
                    variantOption => variantOption[`option${position}`]
                );

                fieldset.querySelectorAll('input[type="radio"]').forEach(optionRadio => {
                    if (availableOptionValues.includes(optionRadio.getAttribute('value'))) {
                        optionRadio.classList.remove('disabled');
                    } else {
                        optionRadio.classList.add('disabled');
                    }
                });
            });
        }

        updateActiveOptionValue(currentOptions) {
            this.getFieldsets().forEach(fieldset => {
                const activeOptionElement = fieldset.querySelector('[data-active-option-value]');
                if (activeOptionElement) {
                    activeOptionElement.textContent = currentOptions[`option${fieldset.dataset.position}`];
                }
            });
        }

        getOptions() {
            const options = {};

            this.getFieldsets().forEach(fieldset => {
                const optionPosition = `option${fieldset.dataset.position}`;
                const checkedOptionRadio = fieldset.querySelector('input[type="radio"]:checked');

                options[optionPosition] = checkedOptionRadio.value;
            });

            return options;
        }

        getCurrentVariant(options) {
            const variant = this.getProduct().variants.find(variant => {
                return variant.option1 === options.option1
                    && variant.option2 === (options.option2 || null)
                    && variant.option3 === (options.option3 || null);
            });

            return typeof variant === 'object' ? variant : null;
        }

        getFieldsets() {
            // each product option has a fieldset
            return this.querySelectorAll('[data-picker-fieldset]');
        }

        getProduct() {
            if (!this.product) {
                const productJsonEl = document.getElementById(this.dataset.productJsonId);
                this.product = JSON.parse(productJsonEl.innerHTML);
            }

            return this.product;
        }
    });

    customElements.define('product-section', class extends HTMLElement {
        constructor() {
            super();

            this.addEventListener('productVariantChanged', this.updateVariant.bind(this));
        }

        updateVariant(event) {
            this.product = event.detail.product;
            this.currentVariant = event.detail.variant;
            this.options = event.detail.options;

            const productForm = this.getProductForm();
            productForm.hideErrorMessage();

            if (this.currentVariant) {
                productForm.disableSubmitButton();
                productForm.updateVariantId(this.currentVariant.id);
                this.updateURL();
                this.updateMedia();
                this.renderProductInfo();
            } else {
                productForm.disableSubmitButton(BryanAnthonys.strings.variant.unavailable);
                productForm.updateVariantId('');
            }
        }

        updateMedia() {
            if (!this.currentVariant || !this.currentVariant.featured_media) {
                return;
            }

            const mediaGalleries = document.querySelectorAll(`[id^="MediaGallery-${this.dataset.section}"]`);
            mediaGalleries.forEach(mediaGallery => {
                mediaGallery.setActiveMedia(`${this.dataset.section}-${this.currentVariant.featured_media.id}`, true);
            });

            const extraVariantMedias = this.querySelectorAll('[data-variant-extra-media]');
            extraVariantMedias.forEach(extraVariantMedia => {
                const variantId = parseInt(extraVariantMedia.dataset.variantId);
                if (variantId === this.currentVariant.id) {
                    extraVariantMedia.classList.add('product__media-item--variant-extra-active');
                    extraVariantMedia.classList.remove('product__media-item--variant-extra');
                } else {
                    extraVariantMedia.classList.remove('product__media-item--variant-extra-active');
                    extraVariantMedia.classList.add('product__media-item--variant-extra');
                }
            });
        }

        updateURL() {
            if (!this.currentVariant || this.dataset.updateUrl === 'false') {
                return;
            }

            const pageUrl = new URL(window.location.href);
            pageUrl.searchParams.set('variant', this.currentVariant.id);
            window.history.replaceState({}, '', pageUrl);
        }

        renderProductInfo() {
            const variantId = this.currentVariant.id;
            const sectionId = this.dataset.section;
            const sectionUrl = this.getProductSectionUrl(variantId, sectionId);

            fetch(sectionUrl)
                .then(response => response.text())
                .then(responseText => {
                    // prevent unnecessary ui changes from abandoned selections
                    if (this.currentVariant.id !== variantId) {
                        return;
                    }

                    const newSectionDom = (new DOMParser()).parseFromString(responseText, 'text/html');

                    const blocksToUpdate = [
                        `price-${sectionId}`, `badges-${sectionId}`, `rewards-${sectionId}`,
                        `description-${sectionId}`, `form-badges-${sectionId}`, `back-in-stock-${sectionId}`,
                        `custom-properties-${sectionId}`, `preorder-info-${sectionId}`, `sticky-add-to-cart-${sectionId}`
                    ];
                    blocksToUpdate.forEach(blockId => {
                        const destination = document.getElementById(blockId);
                        const source = newSectionDom.getElementById(blockId);

                        if (source && destination) {
                            const sourceModal = source.querySelector('modal-dialog');
                            if (sourceModal && !sourceModal.dataset.unmovable) {
                                sourceModal.remove();
                            }

                            // eslint-disable-next-line no-unsanitized/property
                            destination.innerHTML = source.innerHTML;
                        }
                    });

                    const productForm = this.getProductForm();
                    if (this.currentVariant.available) {
                        const text = productForm.querySelector('[data-preorder-item-property]')
                            ? BryanAnthonys.strings.variant.preorder
                            : BryanAnthonys.strings.variant.addToCart;
                        productForm.enableSubmitButton(text);
                    } else {
                        productForm.disableSubmitButton(BryanAnthonys.strings.variant.soldOut);
                    }

                    if (this.querySelector('back-in-stock-opener')) {
                        productForm.hideAddButton();
                    } else {
                        productForm.showAddButton();
                    }
                });
        }

        getProductSectionUrl(variantId, sectionId) {
            const currentPageUrl = new URL(window.location.href);
            const sectionUrlParams = new URLSearchParams();
            sectionUrlParams.set('variant', variantId);
            sectionUrlParams.set('section_id', sectionId);

            const isPreviewLink = Boolean(
                currentPageUrl.pathname === '/products_preview'
                && currentPageUrl.searchParams.get('preview_key')
                && currentPageUrl.hostname.endsWith('.shopifypreview.com')
            );

            let path = isPreviewLink ? '/products_preview' : this.dataset.url;

            if (isPreviewLink) {
                sectionUrlParams.set('preview_key', currentPageUrl.searchParams.get('preview_key'));
            }

            return `${path}?${sectionUrlParams.toString()}`;
        }

        /**
         * @returns {ProductForm}
         */
        getProductForm() {
            if (!this.productForm) {
                this.productForm = document.getElementById(`product-form-${this.dataset.section}`)
                    .closest('product-form');
            }

            return this.productForm;
        }
    });

    customElements.define('product-form', class ProductForm extends HTMLElement {
        initialize() {
            this.form = this.querySelector('form');
            this.form.querySelector('[name=id]').disabled = false;

            if (this.dataset.productIsCustomizable === 'true') {
                this.form.setAttribute('action', '');
            } else {
                this.form.addEventListener('submit', event => {
                    try {
                        this.ajaxSubmit(event);
                    } catch (error) {
                        console.error(error);
                        this.form.submit();
                    }
                });
            }

            this.cart = document.querySelector('cart-drawer');
            this.submitButton = this.querySelector('[type="submit"]');
            this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        ajaxSubmit(event) {
            event.preventDefault();
            if (this.submitButton.getAttribute('aria-disabled') === 'true') {
                return;
            }

            this.hideErrorMessage();

            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.classList.add('loading');

            const formData = new FormData(this.form);
            formData.append('sections', this.cart.getSectionsToRender().map(section => section.id).join(','));
            formData.append('sections_url', BryanAnthonys.pagePath());

            BryanAnthonys.cart.add(formData)
                .then(response => {
                    this.cart.renderContents(response);
                })
                .catch(exception => {
                    if (exception instanceof AjaxException) {
                        this.showErrorMessage(exception.description);
                    } else {
                        console.error(exception);
                    }
                })
                .finally(() => {
                    this.submitButton.classList.remove('loading');
                    if (this.cart.classList.contains('is-empty')) {
                        this.cart.classList.remove('is-empty');
                    }
                    this.submitButton.removeAttribute('aria-disabled');
                });
        }

        disableSubmitButton(newText) {
            this.submitButton.setAttribute('disabled', 'disabled');
            this.updateSubmitButtonText(newText);
        }

        enableSubmitButton(newText) {
            this.submitButton.removeAttribute('disabled');
            this.updateSubmitButtonText(newText);
        }

        showAddButton() {
            this.submitButton.classList.remove('hidden');
        }

        hideAddButton() {
            this.submitButton.classList.add('hidden');
        }

        updateSubmitButtonText(newText) {
            if (!newText) {
                return;
            }

            const span = this.submitButton.querySelector('span');
            if (span) {
                span.textContent = newText;
            } else {
                this.submitButton.textContent = newText;
            }
        }

        updateVariantId(variantId) {
            const input = this.querySelector('[name="id"]');

            input.value = variantId;
            input.dispatchEvent(new Event('change', {bubbles: true}));
        }

        showErrorMessage(errorMessage) {
            this.getErrorMessageWrapper().removeAttribute('hidden');

            this.getErrorMessageWrapper().innerText = errorMessage;

            if (errorMessage) {
                this.dispatchEvent(new Event('productFormHasError'));
            }
        }

        hideErrorMessage() {
            this.getErrorMessageWrapper().setAttribute('hidden', '');
        }

        getErrorMessageWrapper() {
            if (!this.errorMessageWrapper) {
                this.errorMessageWrapper = this.querySelector('.product-form__error-message-wrapper');
            }

            return this.errorMessageWrapper || document.createElement('div');
        }
    });

    customElements.define('sticky-add-to-cart', class extends HTMLElement {
        initialize() {
            this.scrollErrorIntoView = this.scrollErrorIntoView.bind(this);

            if (!BryanAnthonys.device.isMobile()) {
                return;
            }

            this.productForm = document.querySelector('product-form');
            if (!this.productForm) {
                return;
            }

            this.productFormSubmitButton = this.productForm.querySelector('button[type="submit"]');
            if (!this.productFormSubmitButton) {
                return;
            }

            this.submitButton = this.querySelector('[data-cart-add-button]');
            if (!this.submitButton) {
                return;
            }

            this.stickyActiveBodyClass = 'body--with-sticky-add-to-cart';

            this.updateAddToCartButton();
            this.registerAddToCartAction();
            this.registerChangeVariantAction();
            this.listenForSubmitButtonChange();
            this.show();
        }

        updateAddToCartButton() {
            this.submitButton.innerText = this.productFormSubmitButton.innerText;
            if (this.productFormSubmitButton.classList.contains('loading')) {
                this.submitButton.classList.add('loading');
            } else {
                this.submitButton.classList.remove('loading');
            }

            if (this.productFormSubmitButton.hasAttribute('aria-disabled') || this.productFormSubmitButton.hasAttribute('disabled')) {
                this.submitButton.setAttribute('disabled', 'disabled');
                this.submitButton.setAttribute('aria-disabled', 'true');
            } else {
                this.submitButton.removeAttribute('disabled');
                this.submitButton.removeAttribute('aria-disabled');
            }
        }

        registerAddToCartAction() {
            this.submitButton.addEventListener('click', () => {
                if (!this.submitButton.hasAttribute('disabled')) {
                    this.productFormSubmitButton.click();
                }
            });

            this.productForm.addEventListener('productFormHasError', this.scrollErrorIntoView);
        }

        scrollErrorIntoView() {
            if (document.body.classList.contains(this.stickyActiveBodyClass)) {
                this.productForm.getErrorMessageWrapper().scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }
        }

        registerChangeVariantAction() {
            const variantPicker = this.productForm.querySelector('variant-picker');
            if (!variantPicker) {
                return;
            }

            const changeVariantButton = this.querySelector('[data-change-variant-button]');
            if (changeVariantButton) {
                changeVariantButton.addEventListener('click', () => {
                    variantPicker.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                });
            } else {
                this.addEventListener('swatchButtonsVariantChanged', event => {
                    if (event.detail.variant && event.detail.variant.title) {
                        const variantSwatch = variantPicker.querySelector(`[value="${event.detail.variant.title}"]`);
                        variantSwatch.click();
                    }
                });
            }
        }

        listenForSubmitButtonChange() {
            const observer = new MutationObserver(() => {
                this.updateAddToCartButton();
            });

            observer.observe(this.productFormSubmitButton, {subtree: true, childList: true, attributes: true});
        }

        show() {
            const offset = 70;
            (new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    const shouldShow = !entry.isIntersecting && entry.boundingClientRect.y && entry.boundingClientRect.y < offset;
                    const isVisible = document.body.classList.contains(this.stickyActiveBodyClass);

                    if (!shouldShow && isVisible) {
                        document.body.classList.remove(this.stickyActiveBodyClass);
                    } else if (shouldShow && !isVisible) {
                        document.body.classList.add(this.stickyActiveBodyClass);
                    }
                });
            }, {rootMargin: `-${offset}px 0px 0px 0px`})).observe(this.productFormSubmitButton);
        }

        disconnectedCallback() {
            document.body.classList.remove(this.stickyActiveBodyClass);
            this.productForm.removeEventListener('productFormHasError', this.scrollErrorIntoView);
        }
    });

    customElements.define('product-description', class extends HTMLElement {
        initialize() {
            this.headerClass = 'prodfitheader';
            this.contentClass = 'prodfit';

            this.classList.add('product-description--initialized');
            this.normalizeContent();

            this.getHeaders().forEach((descriptionHeader, index) => {
                descriptionHeader.addEventListener(
                    'click',
                    event => this.toggleDescriptionBlock(event.currentTarget)
                );

                if (index === 0) {
                    descriptionHeader.click();
                }

                descriptionHeader.addEventListener('keydown', event => {
                    if (event.code && ['ENTER', 'SPACE'].includes(event.code.toUpperCase())) {
                        event.preventDefault();
                        this.toggleDescriptionBlock(event.currentTarget);
                    }
                });
            });

            const infoAndFit = Array.from(this.getBlocks()).pop();
            const fitGuides = this.querySelector('[data-fit-guides]');
            if (infoAndFit && fitGuides) {
                // eslint-disable-next-line no-unsanitized/method
                infoAndFit.insertAdjacentHTML('beforeend', fitGuides.innerHTML);
            }
        }

        normalizeContent() {
            //first remove any empty divs
            this.getHeaders().forEach(header => {
                if (!header.textContent.trim()) {
                    header.remove();
                }
            });

            const descriptionHeaders = this.getHeaders();

            if (!descriptionHeaders.length) {
                this.querySelectorAll(`.${this.contentClass}`).forEach(
                    descriptionBlock => descriptionBlock.classList.remove(this.contentClass)
                );

                return;
            }

            descriptionHeaders.forEach(descriptionHeader => {
                const nextContent = descriptionHeader.nextElementSibling;
                const nextContentIsValid = nextContent.classList.contains(this.contentClass);

                if (!descriptionHeader.innerHTML) {
                    descriptionHeader.remove();

                    if (nextContentIsValid) {
                        nextContent.classList.remove(this.contentClass);
                    }
                } else if (!nextContentIsValid) {
                    descriptionHeader.classList.remove(this.headerClass);
                }
            });

            this.getHeaders().forEach(descriptionHeader => {
                descriptionHeader.setAttribute("role", "button");
                descriptionHeader.setAttribute("tabindex", "0");
                descriptionHeader.setAttribute('aria-expanded', 'false');
            });
        }

        getHeaders() {
            return this.querySelectorAll(`.${this.headerClass}`);
        }

        getBlocks() {
            return this.querySelectorAll(`.${this.contentClass}`);
        }

        toggleDescriptionBlock(currentHeader) {
            const currentHeaderIsActive = currentHeader.classList.contains('active');

            if (!BryanAnthonys.device.isMobile()) {
                this.getHeaders().forEach(descriptionHeader => {
                    descriptionHeader.classList.remove('active');
                    descriptionHeader.setAttribute('aria-expanded', 'false');
                });
            }

            if (!currentHeaderIsActive) {
                currentHeader.classList.add('active');
                currentHeader.setAttribute('aria-expanded', 'true');
            } else {
                currentHeader.classList.remove('active');
                currentHeader.setAttribute('aria-expanded', 'false');
            }
        }
    });

    customElements.define('product-badges', class extends HTMLElement {
        initialize() {
            this.querySelector('product-block-initializer').remove();

            this.closest('product-section').querySelectorAll('media-gallery')
                .forEach(mediaGallery => {
                    const featuredMedia = mediaGallery.querySelector('.product__media-item.is-active .product-media-container');
                    if (featuredMedia) {
                        featuredMedia.querySelectorAll('.badge').forEach(badge => badge.remove());
                        featuredMedia.insertAdjacentHTML('afterbegin', BryanAnthonys.htmlReviewedForSafety(BryanAnthonys.partiallySanitize(this.innerHTML)));
                    }
                });
        }
    });

    customElements.define('product-shoppay', class extends HTMLElement {
        connectedCallback() {
            this.addEventListener('click', event => {
                const isLinkClick = event.target.nodeName === 'A' || event.target.closest('a');
                if (!isLinkClick) {
                    return;
                }

                const shopPayTerms = event.target.closest('product-section')
                    .querySelector('shopify-payment-terms');

                if (!shopPayTerms) {
                    return;
                }

                const shopPayBanner = shopPayTerms.shadowRoot.querySelector('shop-pay-installments-banner');

                if (!shopPayBanner) {
                    return;
                }

                const learnMoreButton = shopPayBanner.shadowRoot.querySelector('#shopify-installments-cta');
                if (learnMoreButton) {
                    event.preventDefault();

                    learnMoreButton.click();
                }

            });
        }
    });

    customElements.define('product-afterpay', class extends HTMLElement {
        connectedCallback() {
            this.addEventListener('click', event => {
                const isLinkClick = event.target.nodeName === 'A' || event.target.closest('a');
                if (!isLinkClick) {
                    return;
                }

                event.preventDefault();

                if (typeof Afterpay === 'undefined') {
                    const script = document.createElement('script');
                    script.src = this.dataset.scriptUrl;
                    script.onload = () => Afterpay.launchModal();
                    document.body.appendChild(script);
                } else {
                    Afterpay.launchModal();
                }
            });
        }
    });

    customElements.define('product-rewards', class extends HTMLElement {
        initialize() {
            this.productSection = this.closest('product-section');
            this.updatePointsForQuantity = this.updatePointsForQuantity.bind(this);
            this.closeDetailsOnOutsideClick = this.closeDetailsOnOutsideClick.bind(this);

            this.updatePoints();

            this.productSection.addEventListener('change', this.updatePointsForQuantity);
            this.querySelector('details').addEventListener('toggle', this.toggleDetails.bind(this));
        }

        disconnectedCallback() {
            this.productSection.removeEventListener('change', this.updatePointsForQuantity);
        }

        updatePoints() {
            const pointsElement = this.querySelector('[data-reward-points]');
            let points = parseInt(pointsElement.dataset.rewardPoints);

            const productPriceAdjusted = this.productSection.querySelector('product-price [data-sale-price-adjusted]');
            if (productPriceAdjusted) {
                points = Math.floor(parseInt(productPriceAdjusted.dataset.salePriceAdjusted) / 100);
            }

            const quantity = this.productSection.querySelector('[name="quantity"]');
            if (quantity) {
                points *= parseInt(quantity.value);
            }

            pointsElement.textContent = points;
        }

        updatePointsForQuantity(event) {
            if (event.target.name === 'quantity') {
                this.updatePoints();
            }
        }

        toggleDetails(event) {
            if (event.target.open) {
                document.addEventListener('click', this.closeDetailsOnOutsideClick);
            }
        }

        closeDetailsOnOutsideClick(event) {
            if (!event.target.closest('.product-rewards__details-content')){
                setTimeout(
                    () => this.querySelector('details').removeAttribute('open'),
                    50
                );
                document.removeEventListener('click', this.closeDetailsOnOutsideClick);
            }
        }
    });

    customElements.define('product-block-initializer', class extends HTMLElement {
        connectedCallback() {
            if (typeof this.parentElement.initialize === 'function') {
                this.parentElement.initialize();
            }
        }
    });

    customElements.define('preorder-info-opener', class extends HTMLElement {
        connectedCallback() {
            this.addEventListener('click', () => {
                document.querySelectorAll('#PreorderInfoModal').forEach(modal => modal.remove());
                document.body.insertAdjacentHTML('beforeend', `
                    <modal-dialog 
                        id="PreorderInfoModal" class="custom-modal custom-modal--animate-drop custom-modal--centered"
                        data-animated="true" data-animation-duration="400"
                    >
                        <div 
                            class="custom-modal__content" aria-label="Preorder information" role="dialog" 
                            aria-modal="true" tabindex="-1"  style="--max-width: 70rem;"
                        >
                            <div class="custom-modal__content-info">
                                <button
                                    id="ModalClose-PreorderInfoModal" aria-label="Close modal" type="button"
                                    class="custom-modal__toggle custom-modal__toggle--borderless custom-modal__toggle--corner custom-modal__toggle--small"
                                >
                                    <svg class="icon icon-close" aria-hidden="true" focusable="false"><use href="#icon-close"/></svg>
                                </button>
                                <div class="custom-modal__loading" data-preorder-modal-loading>
                                    <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                                        <circle class="path" fill="none" stroke-width="4" cx="33" cy="33" r="30"></circle>
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </modal-dialog>
                `);
                document.getElementById('PreorderInfoModal').show(this.querySelector('button'));

                fetch(`${BryanAnthonys.pagePath()}?section_id=preorder-info`)
                    .then(response => response.text())
                    .then(html => {
                        const preorderLoader = document.querySelector('[data-preorder-modal-loading]');
                        if (preorderLoader && html.includes('shopify-section-preorder-info')) {
                            // eslint-disable-next-line no-unsanitized/method
                            preorderLoader.insertAdjacentHTML('beforebegin', html);
                            preorderLoader.remove();
                        }
                    });
            });
        }
    });
}
